// src/components/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL || `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Verify the reset token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        setError('Invalid reset link.');
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/auth/reset-password/verify?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        if (data.success) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(data.message || 'Invalid or expired reset link.');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Could not verify reset link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();

      if (data.success) {
        setMessage('Password reset successfully. Redirecting to login...');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="logo">
              <span className="logo-icon">🎴</span>
              <h1>JV Rummy</h1>
            </div>
            <p>Set a new password</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message" style={{ color: '#1a5319', marginBottom: '15px' }}>{message}</div>}

          {tokenValid ? (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>
              <button disabled={submitting} type="submit" className="auth-button">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            !message && (
              <div className="auth-footer">
                <p>The reset link is invalid or has expired.</p>
                <Link to="/login" className="auth-link">
                  Go to sign in to request a new link
                </Link>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
